import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  InjectionToken,
  Input,
  Optional,
  Output,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { I18nModule, I18nService } from '@recruitee/i18n';
import { IntegrationKinds } from '@recruitee/integrations/types';
import {
  BadgeComponent,
  BoxComponent,
  ButtonComponent,
  ButtonLoadingDirective,
  DialogConfirmOptions,
  DialogService,
  DividerComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  RtTooltip,
} from '@recruitee/user-interface';

import { HttpErrorHandlerService } from '../../common';
import { IntegrationEventActions } from '../../common/events/events';
import { IntegrationUnion } from '../../common/types';
import { FieldMappingSettingsComponent } from '../../field-mapping/field-mapping-settings/field-mapping-settings.component';
import { RoleAbilityMatrixComponent } from '../role-ability-matrix/role-ability-matrix.component';
import {
  Ability,
  IntegrationListItemBadge,
  IntegrationListItemConfig,
  RemoveIntegrationActionConfig,
  Role,
  RoleAbilityMap,
} from '../types';

export const INTEGRATION_VIEW_CONTEXT = new InjectionToken('Integration items context');

@Component({
  selector: 'rti-integration-list-item',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './integration-list-item.component.html',
  styleUrls: ['./integration-list-item.component.less'],
  standalone: true,
  imports: [
    BadgeComponent,
    BoxComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    CommonModule,
    DividerComponent,
    FieldMappingSettingsComponent,
    HeadingComponent,
    I18nModule,
    IconButtonComponent,
    IconComponent,
    RoleAbilityMatrixComponent,
    RtTooltip,
  ],
})
export class IntegrationListItemComponent {
  @Input() public config: IntegrationListItemConfig | null;

  @Output() private startedEditing: EventEmitter<undefined> = new EventEmitter();
  @Output() private stoppedEditing: EventEmitter<undefined> = new EventEmitter();
  @Output() private integrationRequested: EventEmitter<undefined> = new EventEmitter();
  @Output() private integrationRemoved: EventEmitter<undefined> = new EventEmitter();
  @Output() private integrationUpdated: EventEmitter<undefined> = new EventEmitter();
  @Output() private rolesFormUpdated: EventEmitter<RoleAbilityMap> = new EventEmitter();

  constructor(
    private store: Store,
    private httpErrorHandlerService: HttpErrorHandlerService,
    private dialogService: DialogService,
    private i18n: I18nService,

    @Optional() @Inject(INTEGRATION_VIEW_CONTEXT) public marketplaceViewContext: 'marketplace',
  ) {}

  public get cardBackgroundClass(): string {
    if (this.editing) {
      return 'background-light-blue';
    } else if (this.integrated && this.manageIntegrationButton === 'manage') {
      return 'background-light';
    } else {
      return '';
    }
  }

  public remove(): void {
    this.openConfirmDialog(this.config!.removeActionConfig.dialogConfig).subscribe(confirmed => {
      if (!confirmed) return;

      this.config!.removeActionConfig.handler().subscribe(
        () => {
          this.integrationRemoved.emit();
          if (this.config!.kind) {
            this.store.dispatch(
              IntegrationEventActions.DisintegratedEvent({
                payload: { context: { integrationKind: this.config!.kind } },
              }),
            );
          }
        },
        errorResponse => {
          this.httpErrorHandlerService.default(errorResponse);
        },
      );
    });
  }

  public integrate(): void {
    this.integrationRequested.emit();
  }

  public save(): void {
    this.integrationUpdated.emit();
  }

  public startEditing(): void {
    this.startedEditing.emit();
  }

  public stopEditing(): void {
    this.stoppedEditing.emit();
  }

  public get isNew(): boolean {
    return this.config!.isNew;
  }

  public get showIntegratedBadge(): boolean {
    if ('showIntegratedBadge' in this.config!) {
      return this.config.showIntegratedBadge!;
    } else {
      return this.integrated;
    }
  }

  public get badge(): IntegrationListItemBadge {
    return this.config!.badge();
  }

  public get showPartnerNotPromotedBadge(): boolean {
    if ('showPartnerNotPromotedBadge' in this.config!) {
      return this.config.showPartnerNotPromotedBadge!;
    } else {
      return false;
    }
  }

  public get integrated(): boolean {
    return !!this.integration;
  }

  public get manageIntegrationButton(): 'manage' | 'integrate_now' {
    if ('manageIntegrationButton' in this.config!) {
      return this.config.manageIntegrationButton!;
    } else {
      return 'manage';
    }
  }

  public get integrationName(): string {
    if ('name' in this.config!) {
      return this.config.name!;
    } else {
      return this.i18n.translate(`rti.${this.kind}.name`);
    }
  }

  public get rolesTitle(): string {
    if ('rolesTitle' in this.config!) {
      return this.config.rolesTitle!;
    } else {
      return this.i18n.translate(`rti.${this.kind}.settings.integrations.roles_title`);
    }
  }

  public get rolesSubtitle(): string {
    if ('rolesSubtitle' in this.config!) {
      return this.config.rolesSubtitle!;
    } else {
      return this.i18n.translate(`rti.${this.kind}.settings.integrations.roles_subtitle`);
    }
  }

  public get settingsSubtitle(): string {
    if ('description' in this.config!) {
      return this.config.description!;
    } else {
      return this.i18n.translate(`rti.${this.kind}.settings.integrations.subtitle`);
    }
  }

  public get editing(): boolean {
    return this.config!.editing;
  }

  public get actionsDisabled(): boolean {
    return this.config!.actionsDisabled;
  }

  public get integrateLoading(): boolean {
    return this.config!.integrateLoading;
  }

  public get kind(): IntegrationKinds {
    return this.config!.kind;
  }

  public get integration(): IntegrationUnion | null {
    return this.config!.integration;
  }

  public get removeActionConfig(): RemoveIntegrationActionConfig {
    return this.config!.removeActionConfig;
  }

  public get abilities(): Ability[] | null {
    return this.config!.abilities;
  }

  public get showRoles(): boolean {
    if ('showRoles' in this.config!) {
      return this.config.showRoles!;
    } else {
      return true;
    }
  }

  public get companyRoles(): Role[] {
    return this.config!.companyRoles;
  }

  public get rolesForm(): RoleAbilityMap {
    return this.config!.rolesForm;
  }

  public get showRemoveIntegrationButton(): boolean {
    if ('showRemoveIntegrationButton' in this.config!) {
      return this.config.showRemoveIntegrationButton!;
    } else {
      return true;
    }
  }

  public get canSave(): boolean {
    return this.config!.canSave;
  }

  public updateRolesForm(rolesForm: RoleAbilityMap): void {
    return this.rolesFormUpdated.emit(rolesForm);
  }

  private openConfirmDialog(config: DialogConfirmOptions): Observable<boolean> {
    return this.dialogService.confirm(config);
  }
}
